import React, { useRef, useState, useEffect } from 'react';
import './AudioPlayer.scss';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
};

type AudioPlayerProps = {
    audioSrc: string
};
export default function AudioPlayer({ audioSrc }: AudioPlayerProps) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            const updateDuration = () => setDuration(audio.duration || 0);
            const updateCurrentTime = () => setCurrentTime(audio.currentTime);

            audio.addEventListener('loadedmetadata', updateDuration);
            audio.addEventListener('timeupdate', updateCurrentTime);

            return () => {
                audio.removeEventListener('loadedmetadata', updateDuration);
                audio.removeEventListener('timeupdate', updateCurrentTime);
            };
        }
    }, []);

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (audio) {
            if (isPlaying) {
                audio.pause();
            } else {
                audio.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        if (audio) {
            const rect = event.currentTarget.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const width = rect.width;
            audio.currentTime = (clickX / width) * duration;
        }
    };

    return (
        <div className="audio-player">
            <div className="controls">
                
                <button
                    className="play-pause"
                    onClick={togglePlayPause}
                    aria-label="Play/Pause"
                >

                    {isPlaying ? <PauseIcon/> : <PlayArrowIcon />}

                </button>
            </div>
            <div className="progress-bar">
                <span className="current-time">{formatTime(currentTime)}</span>
                <div className="progress" onClick={handleProgressClick}>
                    <div
                        className="progress-filled"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                    ></div>
                </div>
                <span className="total-time">{formatTime(duration)}</span>
            </div>
            
            <audio ref={audioRef} src={audioSrc} />
        </div>
    );
};
